import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Certificate, Download, Plus, X, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";

export default function Certificates() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", period_start: "", period_end: "" });

  const load = async () => {
    const [c, cs] = await Promise.all([api.get("/certificates"), api.get("/customers?status=approved")]);
    setItems(c.data); setCustomers(cs.data);
  };
  useEffect(() => { load(); }, []);

  const gen = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/certificates/generate", form);
      toast.success(`Certificate ${r.data.cert_no} generated — ${r.data.total_kg}kg / ${r.data.total_co2_kg}kg CO₂`);
      setOpen(false);
      setForm({ customer_id: "", period_start: "", period_end: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (c) => {
    const token = localStorage.getItem("rescigo_token");
    const r = await fetch(`${API_BASE}/certificates/${c.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${c.cert_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell title="Certificates" subtitle="Module 14"
      actions={
        <button data-testid="btn-new-cert" onClick={() => setOpen(true)}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>Generate certificate
        </button>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <table className="w-full text-sm" data-testid="cert-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Cert No</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Period</th>
              <th className="px-6 py-3 text-right">Waste</th>
              <th className="px-6 py-3 text-right">CO₂ saved</th>
              <th className="px-6 py-3 text-right">Bags</th>
              <th className="px-6 py-3">Issued</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-6 py-14 text-center text-[#8A8780]"><Certificate size={28} className="inline-block mb-3 opacity-40"/><br/>No certificates yet.</td></tr>}
            {items.map((c, i) => (
              <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-6 py-3 font-mono font-medium">{c.cert_no}</td>
                <td className="px-6 py-3">{c.customer_snapshot?.name}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{c.period_start} → {c.period_end}</td>
                <td className="px-6 py-3 font-mono text-right">{c.total_kg} kg</td>
                <td className="px-6 py-3 font-mono text-right text-[#2D5A27] font-semibold">{c.total_co2_kg} kg</td>
                <td className="px-6 py-3 font-mono text-right">{c.total_bags}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{new Date(c.issued_at).toLocaleDateString()}</td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => downloadPdf(c)} data-testid={`cert-pdf-${c.id}`}
                          className="text-[#2D5A27] text-xs inline-flex items-center gap-1"><Download size={12}/>PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">Generate certificate</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={gen} className="space-y-4" data-testid="cert-form">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Customer</label>
                <select data-testid="cert-cust" required value={form.customer_id}
                        onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                  <option value="">Select</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Period start</label>
                  <input data-testid="cert-start" type="date" required value={form.period_start}
                         onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Period end</label>
                  <input data-testid="cert-end" type="date" required value={form.period_end}
                         onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
              </div>
              <div className="text-xs text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3">
                Certificate is computed server-side from QR bags received in the period. Generated once — cannot be edited.
              </div>
              <button data-testid="cert-gen" type="submit"
                      className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold"/>Generate
              </button>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}
